import { QueryHookOptions } from '@apollo/client'
import gql from 'graphql-tag'
import { useShowErrorsPopup } from '@/hooks'
import { fragments } from '@/hooks/fragments'
import useDebouncedQuery from '@/hooks/useDebouncedQuery'
import { Query, QuerySearchDealsArgs } from '@/schemaTypes'

const SEARCH_DEALS = gql`
  query searchDeals($args: DealsFiltersArgs!, $withItemsColumns: Boolean!) {
    searchDeals(args: $args) {
      nodes {
        _id
        companyId
        bookingNumber
        status
        createdAt
        dealFinalValues {
          payoutAmount
        }
        customer {
          ...GuestCustomerDataFragment
          ... on Customer {
            _id
            firstname
            lastname
            email
          }
        }
        events {
          __typename
          ... on BaseDealEvent {
            createdAt
            timestamp
          }
        }
        # admin_deals_list_items_columns_enabled: new columns
        ... @include(if: $withItemsColumns) {
          items(
            omitItemValorizationCalculation: true
            omitItemQuestions: true
          ) {
            _id
            title
          }
        }
        # old columns
        ... @skip(if: $withItemsColumns) {
          dealType
          businessUnit
          pickupData {
            transportMode
            standardShippingData {
              sentShippingLabels {
                shippingPartner
              }
            }
          }
          dropoffData {
            transportMode
          }
        }
        initialShopId
        shop {
          _id
          name
        }
      }
      pageInfo {
        skip
        limit
        hasPrevPage
        hasNextPage
        total
      }
    }
  }
  ${fragments.guestCustomerData}
`

type TData = { searchDeals: Query['searchDeals'] }
type TVariables = QuerySearchDealsArgs & { withItemsColumns: boolean }

export function useSearchDeals(opts: QueryHookOptions<TData, TVariables> = {}) {
  const queryResult = useDebouncedQuery<TData, TVariables>(
    SEARCH_DEALS,
    {
      fetchPolicy: 'network-only',
      ...opts,
    },
    200,
  )

  useShowErrorsPopup(queryResult.error)

  return { queryResult }
}
